<template>
  <div :class="modifierClasses" :id="elId" class="Action-item">
    <div class="Action-item__toggler" @click="toggle">
      <div class="Action-item__text">
        <h4 class="Action-item__title">{{ name }}</h4>
        <p v-if="statusText" class="Action-item__description">
          {{ statusText }}
        </p>
      </div>
      <div class="Action-item__status">
        <span class="Action-item__status__inner">{{ statusName }}</span>
      </div>
    </div>
    <a
      v-if="action.totalMessageCount || action.messages.length"
      :class="{
        'Action-item__notifications--message':
          action.totalMessageCount || action.messages.length,
        'Action-item__notifications--message--handled':
          (isApplicant && !defaultOpen) ||
          (!isApplicant &&
            !action.unhandledMessageCount &&
            !(action.messages.length !== action.totalMessageCount))
      }"
      class="Action-item__notifications"
      @click.prevent="openComments"
    >
      <span
        v-if="
          isApplicant ||
            (!action.unhandledMessageCount &&
              action.messages.length === action.totalMessageCount)
        "
        >{{ action.messages.length || action.totalMessageCount }}</span
      >
      <template v-else>
        <span
          v-if="
            action.unhandledMessageCount ||
              action.messages.length !== action.totalMessageCount ||
              uploadNotification
          "
          >!</span
        >
      </template>
    </a>
    <Expander :expanded="expanded">
      <div class="Action-item__content__inner">
        <div class="Action-tabs">
          <a
            :class="{
              'Action-tabs__item--active': !commentsShowing && !notesShowing
            }"
            href
            class="Action-tabs__item"
            @click.prevent="showAttachment"
          >
            <span class="Action-tabs__span">{{ attachmentType }}</span>
          </a>
          <a
            :class="{ 'Action-tabs__item--active': commentsShowing }"
            href
            class="Action-tabs__item"
            @click.prevent="showComments"
          >
            <span class="Action-tabs__span">Kommunicera</span>
            <span
              v-if="action.messages.length || action.totalMessageCount"
              :class="{
                'Action-tabs__counter--handled': !action.messages.isHandled
              }"
              class="Action-tabs__counter"
            >
              <span class="Action-tabs__counter__inner">{{
                action.messages.length || action.totalMessageCount
              }}</span>
            </span>
          </a>
          <a
            v-if="!isApplicant"
            :class="{ 'Action-tabs__item--active': notesShowing }"
            href
            class="Action-tabs__item"
            @click.prevent="showNotes"
          >
            <span v-if="!isApplicant" class="Action-tabs__span"
              >Anteckningar</span
            >
          </a>
        </div>
        <component
          v-if="!commentsShowing && !notesShowing"
          :is="component"
          :action="action"
          :disabled="disabled"
          @resize="resizingTextArea"
          @resizedone="resizingTextAreaDone"
        ></component>
        <ApplicationActionComments
          v-else-if="!attachmentShowing && !notesShowing"
          :action="action"
          :disabled="disabled"
        />
        <ApplicationNotebook
          v-else
          :isApplicationNote="false"
          :id="action.id"
          :disabled="disabled"
          :note="action.note"
          @resize="resizingTextArea"
          @resizedone="resizingTextAreaDone"
        />
      </div>
      <div class="Action-item__content__buttons">
        <button
          class="Button Button--primary Button--primary-inverted"
          @click="collapse"
        >
          Stäng
        </button>
      </div>
    </Expander>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import {
  actionTypeName,
  statusName,
  actionStatusText,
  actionHasUnhandledUpload,
  actionHandleAttachment
} from "@/utils/inquiry-helpers";
import { slugFromKey } from "@/utils/string-helpers";
import Expander from "@/components/Expander";
import ApplicationActionComments from "./ApplicationActionComments";
import ApplicationActionAttachment from "./ApplicationActionAttachment";
import ApplicationNotebook from "./ApplicationNotebook";
import ApplicationActionMeta from "./ApplicationActionMeta";
import ApplicationActionText from "./ApplicationActionText";
import { EventBus } from "@/services/event-bus";

export default {
  name: "ApplicationAction",
  components: {
    Expander,
    ApplicationActionComments,
    ApplicationActionAttachment,
    ApplicationNotebook,
    ApplicationActionMeta,
    ApplicationActionText
  },
  props: {
    action: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    component: {
      type: String,
      required: true
    },
    defaultOpen: {
      type: Boolean,
      default: false
    }
  },
  created() {
    if (this.defaultOpen) {
      this.openComments();
    }
  },
  computed: {
    ...mapGetters("memberStore", ["isApplicant"]),
    ...mapGetters("applicationStore", [
      "actionIsExpanded",
      "actionIsCommentsOpen",
      "actionIsNotesOpen",
      "actionIsAttachmentOpen"
    ]),
    expanded() {
      return this.actionIsExpanded(this.action.id);
    },
    attachmentShowing() {
      return this.actionIsAttachmentOpen(this.action.id);
    },
    commentsShowing() {
      return this.actionIsCommentsOpen(this.action.id);
    },
    notesShowing() {
      return this.actionIsNotesOpen(this.action.id);
    },
    name() {
      return this.action.name || actionTypeName(this.action.type);
    },
    modifierClasses() {
      const modifierClasses = {
        "Action-item--expanded": this.expanded
      };
      if (this.isApplicant && this.action.isValid) {
        if (
          this.action.status === "IN_REVIEW" ||
          this.action.status === "APPROVED"
        ) {
          modifierClasses["Action-item--approved"] = true;
          return modifierClasses;
        } else {
          modifierClasses["Action-item--in-progress"] = true;
          return modifierClasses;
        }
      } else if (!this.isApplicant && this.action.status === "IN_PROGRESS") {
        modifierClasses["Action-item--not-started"] = true;
        return modifierClasses;
      } else if (!this.isApplicant && !this.action.isValid) {
        modifierClasses["Action-item--not-started"] = true;
        return modifierClasses;
      } else if (this.isApplicant && !this.action.isValid) {
        modifierClasses["Action-item--not-started"] = true;
        return modifierClasses;
      } else {
        // eslint-disable-next-line
        modifierClasses[
          `Action-item--${slugFromKey(this.action.status)}`
        ] = true;
        return modifierClasses;
      }
    },
    statusText() {
      return actionStatusText(this.action);
    },
    statusName() {
      if (this.isApplicant && this.action.isValid) {
        if (this.action.status === "IN_REVIEW") {
          return statusName(this.action.status);
        } else if (this.action.status === "APPROVED") {
          return "Godkänd";
        } else {
          return "Redo att skicka in";
        }
      } else if (this.isApplicant && !this.action.isValid) {
        return statusName("NOT_STARTED");
      } else if (
        !this.isApplicant &&
        this.action.status === "IN_REVIEW" &&
        !this.action.isValid
      ) {
        return statusName("NOT_STARTED");
      } else if (
        (!this.isApplicant && this.action.status === "IN_PROGRESS") ||
        !this.action.isValid
      ) {
        return statusName("NOT_STARTED");
      } else if (!this.isApplicant && !this.action.isValid) {
        return statusName("NOT_STARTED");
      } else {
        return statusName(this.action.status);
      }
    },
    uploadNotification() {
      return actionHasUnhandledUpload(this.action);
    },
    elId() {
      return "inquiry-action-" + this.action.id;
    },
    attachmentType() {
      return actionHandleAttachment(this.action);
    }
  },
  methods: {
    ...mapActions("applicationStore", [
      "toggleExpandAction",
      "expandAction",
      "collapseAction",
      "openActionComments",
      "closeActionComments",
      "openActionNotes",
      "closeActionNotes",
      "openActionAttachment",
      "closeActionAttachment"
    ]),
    toggle() {
      this.toggleExpandAction(this.action);
      EventBus.$emit("polling:setup");
    },
    collapse() {
      this.collapseAction(this.action);
      EventBus.$emit("polling:setup");
    },
    showAttachment() {
      this.openTabs("attachment");
    },
    showComments() {
      this.openTabs("comment");
    },
    showNotes() {
      this.openTabs("note");
    },
    openTabs(tab) {
      this.closeActionComments(this.action);
      this.closeActionNotes(this.action);
      this.closeActionAttachment(this.action);

      if (tab === "attachment") {
        this.openActionAttachment(this.action);
      } else if (tab === "comment") {
        this.openActionComments(this.action);
      } else {
        this.openActionNotes(this.action);
      }
      EventBus.$emit("expander:resize");
      EventBus.$emit("polling:setup");
    },
    openComments() {
      if (!this.expanded) {
        this.expandAction(this.action);
      }
      if (!this.commentsShowing) {
        this.showComments();
      }
      EventBus.$emit("expander:resize");
    },
    resizingTextArea() {
      EventBus.$emit("expander:resize", true);
    },
    resizingTextAreaDone() {
      EventBus.$emit("expander:resizedone");
    }
  }
};
</script>

<style lang="scss" scoped>
.Action-item {
  background-color: $color-white-two;
  box-shadow: $box-shadow-default;
  border: 1px solid $color-white-three;
  border-bottom: 0;
  color: black;
  position: relative;
  margin-right: $spacing-L;
  z-index: $z-base;

  @include media-from($beta-width) {
    margin-right: $spacing-L * 2;
  }

  &:first-of-type {
    border-top-left-radius: $border-radius-default;
    border-top-right-radius: $border-radius-default;
  }

  &:last-of-type {
    border-bottom-left-radius: $border-radius-default;
    border-bottom-right-radius: $border-radius-default;
    border-bottom: 1px solid $color-white-three;
  }

  &:only-of-type {
    border-radius: $border-radius-default;
  }

  &:before {
    border-radius: 2px;
    bottom: 0;
    content: "";
    left: 0;
    margin: 4px;
    position: absolute;
    top: 0;
    width: 3px;
  }

  &--in-progress:before,
  &--in-review:before {
    background-color: $color-in-progress-light-tan;
  }

  &--approved:before,
  &--confirmed:before,
  &--submitted:before {
    background-color: $color-approved-green;
  }

  &__toggler {
    @extend %text-S;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $spacing-L $spacing-L $spacing-L $spacing-L;
    position: relative;
    width: 100%;

    @include media-from($beta-width) {
      padding: $spacing-L ($spacing-L + $spacing) $spacing-L $spacing-L;
    }

    &:before {
      @include inline-svg($chevron-separator);
      content: "";
      position: absolute;
      right: $spacing;
      top: 50%;
      left: initial;
      margin-top: inline-svg-height($chevron-separator) * -0.5;
    }
  }

  &__description {
    color: $color-greyish-brown;
    margin-top: 10px;
  }

  &__status {
    @extend %text-XS;
    align-items: center;
    display: flex;
    font-weight: $font-bold;
    justify-content: center;
    position: relative;
    flex: none;
    width: 68px;
    height: 48px;

    .Action-item--in-progress &,
    .Action-item--in-review & {
      color: $color-in-progress-leather;
    }

    .Action-item--approved &,
    .Action-item--confirmed &,
    .Action-item--submitted & {
      color: $color-navy-green;
    }

    .Action-item--not-eligible &,
    .Action-item--not-started & {
      color: $color-warm-grey;
    }

    &:before {
      content: "";
      position: absolute;
      top: 30%;
      left: 50%;
      transform: translate(-50%, -50%);

      .Action-item--in-progress &,
      .Action-item--in-review & {
        @include inline-svg($status-in-progress);
      }

      .Action-item--approved &,
      .Action-item--confirmed &,
      .Action-item--submitted & {
        @include inline-svg($status-approved);
      }

      .Action-item--not-eligible & {
        @include inline-svg($status-not-eligable);
      }
    }

    &:after {
      content: "";
      border: 1px solid $color-white-three;
      border-radius: 50%;
      height: 24px;
      width: 24px;
      position: absolute;
      top: 30%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &__inner {
      display: block;
      margin-top: 36px;
      text-align: center;
    }
  }

  &__notifications {
    position: absolute;
    color: $color-nice-blue;
    right: $spacing-L * -1;
    top: $spacing-L + 15px;
    font-weight: $font-bold;

    &--message {
      @include message();
      right: $spacing * -2;
      @include media-from($beta-width) {
        right: $spacing-L * -2;
      }
    }
  }

  &__content {
    &__inner {
      padding: $spacing $spacing $spacing $spacing-L;
      @include media-from($beta-width) {
        padding: $spacing $spacing-L $spacing $spacing-L;
      }
    }

    &__buttons {
      background-color: $color-dark-grey-blue;
      padding: $spacing $spacing-L;
      position: relative;
      text-align: right;

      &:before {
        content: "";
        background-color: $color-white-two;
        width: 3px;
        height: $spacing;
        position: absolute;
        top: -$spacing;
        left: 4px;
      }
    }
  }
}

.Action-item {
  &__toggler {
    cursor: pointer;
  }
}

.Action-tabs {
  border-bottom: 1px solid $color-border;

  &__item {
    @extend %text-S;
    @extend %text-subtle-2;
    display: inline-block;
    font-weight: $font-bold;
    padding: 0 10px;
    position: relative;

    &--active {
      border-bottom: 3px solid black;

      .Action-tabs__span {
        color: black;
      }
    }
  }

  &__span {
    display: inline-block;
    padding: 12px 0;
  }

  &__counter {
    display: inline-block;
    margin-left: 4px;
    color: white;
    position: relative;
    text-align: center;
    height: 24px;
    width: 24px;

    &:after {
      content: "";
      background: $color-nice-blue;
      border: 1px solid $color-white-three;
      border-radius: 50%;
      height: 100%;
      position: absolute;
      right: 0;
      top: -4px;
      width: 100%;
    }

    &--handled {
      color: black;

      &:after {
        background: $color-white-two;
      }
    }

    &__inner {
      position: relative;
      z-index: $z-base;
    }
  }
}
</style>
